/**
 * FriendsWidget - Display mutual friends list
 * Reusable component for both desktop and mobile layouts
 */

import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import type { ThemeTokens } from '../../../shared/theme/tokens';
import { getFriends } from '../../../lib/api-client';

interface FriendsWidgetProps {
  theme: ThemeTokens;
  className?: string;
  style?: React.CSSProperties;
  maxDisplay?: number;
  /** Hide the header/title */
  showHeader?: boolean;
  /** Additional className for the header */
  headerClassName?: string;
  /** Additional className for the content area */
  contentClassName?: string;
  /** Compact mode - reduces padding and spacing */
  compact?: boolean;
  /** Navigation callback - if not provided, uses window.location.href */
  onNavigate?: (path: string) => void;
}

interface Friend {
  id: string;
  username: string;
  displayName?: string;
  avatar?: string;
}

export const FriendsWidget: React.FC<FriendsWidgetProps> = ({
  theme,
  className = '',
  style = {},
  maxDisplay = 20,
  showHeader = true,
  headerClassName = '',
  contentClassName = '',
  compact = false,
  onNavigate,
}) => {
  // Default navigation fallback
  const navigate = onNavigate || ((path: string) => {
    window.location.href = path;
  });
  const [friends, setFriends] = useState<Friend[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadFriends();

    // Listen for follow/unfollow events to refresh the list
    const handleFollowingUpdated = () => {
      loadFriends();
    };

    window.addEventListener('followingUpdated', handleFollowingUpdated);
    return () => window.removeEventListener('followingUpdated', handleFollowingUpdated);
  }, []);

  const loadFriends = async () => {
    setIsLoading(true);
    try {
      const data = await getFriends();
      setFriends(data || []);
    } catch (error) {
      console.error('Failed to load friends:', error);
      setFriends([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`rounded-lg overflow-hidden ${className}`}
      style={{
        backgroundColor: theme.cardBg || 'rgba(255, 255, 255, 0.05)',
        borderColor: `${theme.accentColor}40`,
        border: '1px solid',
        ...style,
      }}
    >
      {showHeader && (
        <h3
          className={`font-bold text-center border-b ${compact ? 'py-2' : 'py-3'} ${headerClassName}`}
          style={{
            color: theme.textColor,
            borderColor: `${theme.accentColor}40`,
          }}
        >
          <Users className="inline-block w-4 h-4 mr-2" />
          Friends
        </h3>
      )}

      <div className={`${compact ? 'p-2' : 'p-4'} ${contentClassName}`}>
        {isLoading ? (
          <div className="text-center py-4" style={{ color: theme.textColor + '99' }}>
            Loading...
          </div>
        ) : friends.length > 0 ? (
          <>
            <div className="space-y-3 mb-4">
              {friends.slice(0, maxDisplay).map((friend) => (
                <div
                  key={friend.id}
                  onClick={() => navigate(`/user/${friend.username}`)}
                  className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                  style={{
                    backgroundColor: theme.accentColor + '10',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: theme.accentColor + '40',
                    }}
                  >
                    <span style={{ color: theme.textColor, fontSize: '16px' }}>
                      {friend.displayName?.[0] || friend.username[0]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate" style={{ color: theme.textColor }}>
                      {friend.displayName || friend.username}
                    </p>
                    <p className="text-sm truncate" style={{ color: theme.textColor + '99' }}>
                      @{friend.username}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {friends.length > maxDisplay && (
              <button
                onClick={() => navigate('/friends')}
                className="w-full py-2 px-4 rounded-lg font-medium transition-all hover:opacity-80"
                style={{
                  backgroundColor: theme.accentColor + '20',
                  color: theme.textColor,
                  border: `1px solid ${theme.accentColor}40`,
                }}
              >
                See All Friends
              </button>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-6">
            <Users size={48} style={{ color: theme.textColor + '40', marginBottom: '12px' }} />
            <p style={{ color: theme.textColor + '99', fontSize: '14px', marginBottom: '8px' }}>
              No friends yet
            </p>
            <p style={{ color: theme.textColor + '66', fontSize: '12px' }}>
              Follow people back to become friends
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
